import {
	type GetAllContactNamesQueryVariables,
	type GetAllMembersOfQuery,
	type GetAllMembersOfQueryVariables,
	useGetAllMembersOfLazyQuery,
} from "@repo/server-api";
import { useCallback } from "react";

import { useAsyncSelectCursorPaginatedLazyQuery } from "./AsyncSelectCursorPaginatedLazyQuery";

import type { AsyncSelectSearchHook } from "../BoundAsyncSelect";
import type { IdWithName } from "@repo/shared/common";

export function useAsyncSelectMembersOfQuery(
	contactId: string | null,
	types: GetAllContactNamesQueryVariables["types"] = null,
	andTypes: boolean = false,
	showDeleted: boolean = false,
): AsyncSelectSearchHook<IdWithName> {
	const [fetchContacts] = useGetAllMembersOfLazyQuery();

	const lazyQueryWrapper = useCallback(
		async ({
			variables,
		}: {
			variables: GetAllMembersOfQueryVariables;
		}): Promise<{ data?: GetAllMembersOfQuery }> => {
			if (variables.contactId !== "") {
				return fetchContacts({ variables });
			} else {
				return Promise.resolve({
					data: undefined,
				});
			}
		},
		[fetchContacts],
	);

	const buildVariables = useCallback(
		(search: string, cursor: string | null): GetAllMembersOfQueryVariables => ({
			contactId: contactId ?? "",
			name: search || null,
			types,
			andTypes,
			showDeleted,
			first: 20,
			after: cursor,
		}),
		[contactId, types, andTypes, showDeleted],
	);

	const extractItems = (data: GetAllMembersOfQuery | undefined): IdWithName[] =>
		data?.allMembersOf?.nodes?.map((n) => ({
			id: n.id,
			name: n.displayName,
		})) ?? [];

	const extractEndCursor = (data: GetAllMembersOfQuery | undefined): string | null =>
		data?.allMembersOf?.pageInfo?.endCursor ?? null;

	const extractHasNextPage = (data: GetAllMembersOfQuery | undefined): boolean =>
		data?.allMembersOf?.pageInfo?.hasNextPage ?? false;

	return useAsyncSelectCursorPaginatedLazyQuery<
		GetAllMembersOfQueryVariables,
		GetAllMembersOfQuery,
		IdWithName
	>(lazyQueryWrapper, buildVariables, extractItems, extractEndCursor, extractHasNextPage);
}
